import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

const VIEWPORT_PADDING = 12;
const MENU_GAP = 6;

export default function CustomSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  leadingIcon: LeadingIcon,
  ariaLabel = 'Select an option',
  disabled = false,
  buttonClassName = '',
  menuClassName = '',
  optionClassName = '',
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectedOption = options.find(
    (option) => String(option.value) === String(value),
  );

  const updatePosition = () => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    if (!trigger) return;

    const menuHeight = menuRef.current?.offsetHeight || 240;
    const menuWidth = Math.max(
      menuRef.current?.offsetWidth || 0,
      trigger.width,
    );
    const spaceBelow = window.innerHeight - trigger.bottom;
    const opensAbove = spaceBelow < menuHeight + MENU_GAP + VIEWPORT_PADDING;
    const maxTop = Math.max(
      VIEWPORT_PADDING,
      window.innerHeight - menuHeight - VIEWPORT_PADDING,
    );
    const top = opensAbove
      ? Math.max(VIEWPORT_PADDING, trigger.top - menuHeight - MENU_GAP)
      : Math.min(trigger.bottom + MENU_GAP, maxTop);
    const left = Math.min(
      Math.max(VIEWPORT_PADDING, trigger.right - menuWidth),
      Math.max(
        VIEWPORT_PADDING,
        window.innerWidth - menuWidth - VIEWPORT_PADDING,
      ),
    );

    setPosition({ top, left, width: trigger.width });
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedTrigger = triggerRef.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedMenu) setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((current) => !current);
    }
  };

  const handleSelect = (option) => {
    if (option.disabled) return;
    onChange?.(option.value, option);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`inline-flex h-9 items-center justify-between gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-700 shadow-sm outline-none transition hover:border-[#375AE7]/35 hover:bg-[#EEF2FF] hover:text-[#375AE7] focus:border-[#375AE7] focus:ring-4 focus:ring-[#375AE7]/10 disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`}
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          {LeadingIcon ? (
            <LeadingIcon className="h-3.5 w-3.5 shrink-0 text-[#375AE7]" />
          ) : null}
          <span className="truncate">
            {selectedOption?.label ?? placeholder}
          </span>
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${
            open ? 'rotate-180 text-[#375AE7]' : ''
          }`}
        />
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label={ariaLabel}
              onMouseDown={(event) => event.stopPropagation()}
              style={{
                top: position.top,
                left: position.left,
                minWidth: position.width,
              }}
              className={`fixed z-[110] max-h-[min(280px,calc(100vh-1.5rem))] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-[0_18px_36px_-18px_rgba(15,23,42,0.45)] ${menuClassName}`}
            >
              {options.length ? (
                options.map((option) => {
                  const selected = String(option.value) === String(value);
                  return (
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={option.disabled}
                      key={String(option.value)}
                      onClick={() => handleSelect(option)}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left text-xs font-semibold transition ${
                        selected
                          ? 'bg-[#EEF2FF] text-[#375AE7]'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      } ${option.disabled ? 'cursor-not-allowed opacity-40' : ''} ${optionClassName}`}
                    >
                      <span className="truncate">{option.label}</span>
                      {selected ? (
                        <Check
                          className="h-3.5 w-3.5 shrink-0"
                          strokeWidth={3}
                        />
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <p className="px-2.5 py-2 text-xs text-slate-400">No options</p>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
